import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import {
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
} from "@material-ui/core"
import { Search as SearchIcon } from "@material-ui/icons"
import { useHistory } from "react-router-dom"
import { paths } from "../../routes/paths"
import styles from "./AdminSubscriptionListContainer.module.scss"

function AdminSubscriptionListContainer({
  subscriptions = [],
  loading,
  error,
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  onPageChange,
  filters = {},
  onFilterChange,
}) {
  const history = useHistory()
  const [localFilters, setLocalFilters] = useState(filters)

  useEffect(() => {
    setLocalFilters(filters)
  }, [filters])

  const handleViewSubscription = (userId) => {
    history.push(paths.dashboardSubscription(userId))
  }

  const handleFilterUpdate = (key, value) => {
    const newFilters = { ...localFilters, [key]: value }
    setLocalFilters(newFilters)
    if (onFilterChange) {
      onFilterChange(newFilters)
    }
  }

  const handleSearchChange = (event) => {
    const value = event.target.value
    setLocalFilters({ ...localFilters, search: value })
    clearTimeout(window.searchTimeout)
    window.searchTimeout = setTimeout(() => {
      if (onFilterChange) {
        onFilterChange({ ...localFilters, search: value })
      }
    }, 500)
  }

  const getCompanyTypeLabel = (subscription) => {
    if (subscription.solopreneur) {
      return "Solopreneur"
    }
    if (subscription.describe_company === "professional") {
      return "Professional"
    }
    if (subscription.company_type === "company_seeker") {
      return "Company Seeker"
    }
    if (subscription.company_type === "company_provider") {
      return "Company Provider"
    }
    return subscription.company_type || "Unknown"
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Typography variant="h4" className={styles.title}>
            Error
          </Typography>
          <Typography variant="body1" className={styles.error}>
            {error}
          </Typography>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Typography variant="h4" className={styles.title}>
          Subscriptions
        </Typography>
        <Typography variant="body1" className={styles.subtitle}>
          Total subscriptions: {totalCount}
        </Typography>

        <Box className={styles.filtersSection} mb={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by company or admin email..."
                value={localFilters.search || ""}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={localFilters.status || ""}
                  onChange={(e) => handleFilterUpdate("status", e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="trial">Trial</MenuItem>
                  <MenuItem value="none">None</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Company Type</InputLabel>
                <Select
                  value={localFilters.company_type || ""}
                  onChange={(e) => handleFilterUpdate("company_type", e.target.value)}
                  label="Company Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="company_seeker">Company Seeker</MenuItem>
                  <MenuItem value="company_provider">Company Provider</MenuItem>
                  <MenuItem value="solopreneur">Solopreneur</MenuItem>
                  <MenuItem value="professional">Professional</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                variant="outlined"
                value={localFilters.start_date || ""}
                onChange={(e) => handleFilterUpdate("start_date", e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                variant="outlined"
                value={localFilters.end_date || ""}
                onChange={(e) => handleFilterUpdate("end_date", e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />
            </Grid>
          </Grid>
        </Box>

        <TableContainer component={Paper} className={styles.tableContainer}>
          <Table className={styles.table}>
            <TableHead>
              <TableRow>
                <TableCell>User Email</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Seats</TableCell>
                <TableCell>Purchase Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions && subscriptions.length > 0 ? (
                subscriptions.map((sub) => (
                  <TableRow key={sub.id}>
                    <TableCell>{sub.user_email}</TableCell>
                    <TableCell>{getCompanyTypeLabel(sub)}</TableCell>
                    <TableCell>
                      <span className={`${styles.status} ${styles[sub.status]}`}>
                        {sub.status}
                      </span>
                    </TableCell>
                    <TableCell>{sub.seats}</TableCell>
                    <TableCell>
                      {sub.subscription_purchase_date
                        ? new Date(sub.subscription_purchase_date).toLocaleDateString()
                        : new Date(sub.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleViewSubscription(sub.user_id)}
                        className={styles.openButton}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No subscriptions found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={currentPage - 1}
            onPageChange={onPageChange}
            rowsPerPage={20}
            rowsPerPageOptions={[20]}
            className={styles.pagination}
          />
        </TableContainer>
      </div>
    </div>
  )
}

AdminSubscriptionListContainer.propTypes = {
  subscriptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string,
      user_email: PropTypes.string,
      user_id: PropTypes.number,
      company_type: PropTypes.string,
      describe_company: PropTypes.string,
      solopreneur: PropTypes.bool,
      status: PropTypes.string,
      seats: PropTypes.number,
      subscription_purchase_date: PropTypes.string,
      subscription_pause_date: PropTypes.string,
      extended_trial: PropTypes.bool,
      stripe_subscription_id: PropTypes.string,
      created_at: PropTypes.string,
    }),
  ),
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  totalCount: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  filters: PropTypes.object,
  onFilterChange: PropTypes.func,
}

AdminSubscriptionListContainer.defaultProps = {
  subscriptions: [],
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  error: null,
  filters: {},
  onFilterChange: null,
}

export default AdminSubscriptionListContainer
